import {
	DocAudience,
	DocCode,
	DocCommand,
	DocHeading,
	DocList,
	DocPage,
	DocParagraph,
	DocScrollTable,
	DocSectionLink,
	DocSubheading,
	DocTabs,
	DocTerm,
} from "../DocElements";

const REQUIREMENT_COLUMNS = [{ label: "項目", nowrap: true }, { label: "必須" }, { label: "推奨" }];

const REQUIREMENT_ROWS = [
	{ key: "os", cells: ["OS", "Ubuntu 24.04（DGX OSを含む）", "―"] },
	{ key: "cpu", cells: ["CPU", "arm64かx86_64", "16コア以上"] },
	{ key: "memory", cells: ["メモリ", "起動するサイズのメモリ以上", "64GB以上"] },
	{
		key: "disk",
		cells: [
			"ディスク",
			"データの保存先（ふつうはホームのディスク）に20GiB以上の空き",
			"100GB以上の空き",
		],
	},
	{ key: "gpu", cells: ["GPU", "不要", "NVIDIAのGPU（DGX Sparkなど）"] },
];

function GuiInstallSteps() {
	return (
		<>
			<DocParagraph>デバイスに画面があるときは、マウスで操作して登録できます。</DocParagraph>
			<DocList ordered>
				<li>デバイスで.debをダウンロードします。</li>
				<li>
					.debを右クリックして「別のアプリケーションで開く」から「App
					Center」を選び、「インストール」を押して管理者のパスワードを入力します。
				</li>
				<li>
					アプリ一覧から「Izanagi
					デバイスを登録」を開きます。必要に応じて、管理者のパスワードや、USB機器を使う許可（「
					<DocSectionLink id="usb">USB機器やロボットとつなぐ</DocSectionLink>
					」を参照）を聞かれます。ホームのディスクの空きが20GiBより少ないときは、データを保存するディスクも選びます。保存先はあとから変えられません。
				</li>
				<li>
					最初の画面に、Izanagiで発行した登録用のコードを貼り付けて「登録を始める」を押します。
				</li>
				<li>
					確認コードが表示されます。コードの右のボタンでコピーし、Izanagiの「デバイスを追加」の手順4に入力して登録します。
				</li>
			</DocList>

			<DocSubheading>登録したあとの操作</DocSubheading>
			<DocParagraph>
				登録したあとに「Izanagi デバイスを登録」を開くと、デバイスの状態と次のボタンが表示されます。
			</DocParagraph>
			<DocList>
				<li>
					<DocTerm>ランナーを再起動</DocTerm>
					：ランナーを起動し直します。
				</li>
				<li>
					<DocTerm>登録をやり直す</DocTerm>
					：このデバイスの登録を消して、登録用のコードから登録し直します。前の登録はIzanagiのデバイスの一覧に残るので、デバイス画面の「解除」で解除してください。
				</li>
				<li>
					<DocTerm>詳細設定</DocTerm>
					：社内プロキシや独自CAを設定します。
				</li>
				<li>
					<DocTerm>GPU の設定を作り直す</DocTerm>
					：GPUの設定に問題があるときだけ表示されます。
				</li>
				<li>
					<DocTerm>アンインストール</DocTerm>
					：このデバイスからIzanagiを取り除きます（「
					<DocSectionLink id="uninstall">デバイスからアンインストールする</DocSectionLink>
					」を参照）。
				</li>
			</DocList>
		</>
	);
}

function TerminalInstallSteps() {
	return (
		<>
			<DocParagraph>
				デバイスのターミナルでコマンドを実行して登録します。画面の無いサーバーでも、同じ手順で登録できます。
			</DocParagraph>
			<DocList ordered>
				<li>
					.debを置いたフォルダで、次のコマンドを実行します。
					<DocCommand command="sudo apt install ./izanagi-device_*.deb" />
				</li>
				<li>
					エージェントを動かすアカウントで、設定アプリを起動します。画面があってもターミナルで操作したいときは、
					<DocCode>--text</DocCode>
					を付けます。
					<DocCommand command="/opt/izanagi-device/bin/izanagi-device-setup" />
				</li>
				<li>
					プロンプトに登録用のコードを貼り付けると確認コードが表示されるので、Izanagiの「デバイスを追加」の手順4に入力します。管理者の権限が要る作業では、sudoのパスワードを聞かれます。
				</li>
			</DocList>

			<DocSubheading>登録したあとのキー操作</DocSubheading>
			<DocParagraph>登録したあとに設定アプリを起動すると、キーで次の操作ができます。</DocParagraph>
			<DocList>
				<li>
					<DocCode>r</DocCode>
					：ランナーを再起動
				</li>
				<li>
					<DocCode>n</DocCode>
					：登録をやり直す
				</li>
				<li>
					<DocCode>s</DocCode>
					：詳細設定（社内プロキシ・独自CA）
				</li>
				<li>
					<DocCode>g</DocCode>
					：GPUの設定を作り直す（GPUの設定に問題があるときだけ）
				</li>
				<li>
					<DocCode>u</DocCode>
					：アンインストール
				</li>
				<li>
					<DocCode>q</DocCode>
					：閉じる（ランナーは動き続けます）
				</li>
			</DocList>
		</>
	);
}

const INSTALL_TABS = [
	{ key: "gui", label: "画面", content: <GuiInstallSteps /> },
	{ key: "terminal", label: "ターミナル", content: <TerminalInstallSteps /> },
];

export function DevicesPage() {
	return (
		<DocPage title="ローカルデバイス">
			<DocParagraph>
				手元のLinuxマシン（DGX
				Sparkなど）を組織にデバイスとして登録すると、エージェントをそのデバイスで動かせます。デバイスのGPUや、USBでつないだロボット・カメラもエージェントから使えます。チャットや起動・停止は、クラウドのエージェントと同じくIzanagiの画面から行えます。
			</DocParagraph>
			<DocParagraph>
				デバイスで動くエージェントは、組織の管理者が作成時に「実行場所」でデバイスを選んで作ります。
			</DocParagraph>

			<DocHeading>ライセンス</DocHeading>
			<DocParagraph>
				デバイス1台の登録には、ライセンスが1つ必要です。ライセンスはご契約に応じて組織に付与されます。ライセンスの料金は、付与された日とそれ以降の毎月の更新日に、組織のクレジットから引かれます。ライセンスを追加したいときは、Monomythにお問い合わせください。
			</DocParagraph>

			<DocSubheading>ライセンスの空き</DocSubheading>
			<DocParagraph>
				デバイス画面の見出しの下に、「登録 2 台 / ライセンス 3 台 · 空き 1
				台」のように、登録しているデバイス・ライセンス・空いているライセンスの数が表示されます。
			</DocParagraph>

			<DocSubheading>一覧の「ライセンス」列</DocSubheading>
			<DocList>
				<li>
					<DocTerm>次の更新日</DocTerm>
					：次にクレジットから引かれる日です。
				</li>
				<li>
					<DocTerm>更新日 (11/5) に解除</DocTerm>
					：ライセンスの解約が予約されています。更新日にデバイスの登録が解除され、そのデバイスのエージェントは使えなくなります。
				</li>
				<li>
					<DocTerm>支払い待ち (11/5 から)</DocTerm>
					：組織のクレジットが足りず、更新の分が引けなかった状態です。支払い待ちの間は、そのデバイスで動いているエージェントが止まり、起動もできません。クレジットが追加されると、また使えるようになります。支払いについては、Monomythにお問い合わせください。
				</li>
			</DocList>

			<DocSubheading>空きが無いとき</DocSubheading>
			<DocParagraph>
				空いているライセンスが無いと、「デバイスを追加」の手順1に理由が表示され、.debのダウンロードと登録用のコードの発行ができません。
			</DocParagraph>
			<DocParagraph>
				使っていないデバイスを一覧の「解除」で解除すると、そのライセンスが空き、別のデバイスの登録に使えます。ただし、「ライセンス」列が「支払い待ち」か「更新日に解除」のデバイスでは、解除してもライセンスは空きません。
			</DocParagraph>

			<DocHeading id="requirements">動作環境</DocHeading>
			<DocScrollTable columns={REQUIREMENT_COLUMNS} rows={REQUIREMENT_ROWS} />
			<DocParagraph>
				Ubuntu 24.04以外のバージョン（22.04など）にはインストールできません。
			</DocParagraph>
			<DocParagraph>
				エージェントを起動するときは、デバイスのCPU数またはメモリの総量を超えるサイズは選べません。CPUとメモリが推奨を満たしていれば、デバイス専用のサイズ「4XL
				(16 vCPU / 56 GiB)」まで動かせます。
			</DocParagraph>
			<DocParagraph>1台のデバイスに作れるエージェントは4つまでです。</DocParagraph>
			<DocParagraph>
				登録したあとにデータの保存先の空きが10GiBを下回ると、エージェントを起動できません。エージェントの環境とファイルはデータの保存先に置かれるため、推奨の空きを用意してください。
			</DocParagraph>
			<DocParagraph>
				GPUを使うには、NVIDIAのGPUとドライバが必要です。NVIDIA以外のGPUは使えません。
			</DocParagraph>

			<DocHeading>デバイスを追加する</DocHeading>
			<DocAudience>組織の管理者向け</DocAudience>
			<DocParagraph>
				デバイスを登録できるのは、組織の管理者だけです。登録したデバイスは組織で共有して使います。
			</DocParagraph>

			<DocSubheading>用意するもの</DocSubheading>
			<DocList>
				<li>
					「<DocSectionLink id="requirements">動作環境</DocSectionLink>」を満たすマシン
				</li>
				<li>デバイスの管理者（sudo）のパスワード</li>
				<li>
					Izanagiにつながるネットワーク。社内プロキシや独自CAが必要なネットワークでは、設定アプリの「詳細設定」で設定します。
				</li>
				<li>空いているライセンス</li>
				<li>GPUを使う場合は、NVIDIAのドライバ。NVIDIA Container Toolkitは任意です。</li>
			</DocList>
			<DocParagraph>
				インストールしたあとの作業は、エージェントを動かすLinuxのアカウントで行ってください。デバイスのランナー（エージェントを動かすプログラム）は、登録したアカウントで動き続けます。
			</DocParagraph>

			<DocSubheading>登録の流れ</DocSubheading>
			<DocList ordered>
				<li>サイドバーの「デバイス」を開き、右上の「デバイスを追加」を押します。</li>
				<li>
					手順1で「デバイスの CPU」を選び、.debをダウンロードします。DGX
					Sparkなどはarm64、IntelやAMDのCPUならx86_64です。
				</li>
				<li>
					デバイスに.debをインストールします。やり方は「
					<DocSectionLink id="install">インストールして登録する</DocSectionLink>
					」を見てください。
				</li>
				<li>
					手順3で「登録用のコードを発行」を押してコードをコピーし、デバイスの設定アプリに貼り付けます。
				</li>
				<li>デバイスに表示された確認コードを、手順4に入力して登録します。</li>
			</DocList>
			<DocParagraph>
				登録したデバイスは一覧に追加され、「接続待ち」「準備中」を経て「オンライン」になります。オンラインになると、そのデバイスでエージェントを動かせます。
			</DocParagraph>

			<DocSubheading>登録用のコード</DocSubheading>
			<DocParagraph>
				登録用のコードは10分間有効で、1台の登録にだけ使えます。他人には渡さないでください。期限が切れたときは、「新しいコードを発行」で発行し直せます。発行し直しても、使っていない前のコードは期限まで使えます。
			</DocParagraph>

			<DocSubheading>確認コード</DocSubheading>
			<DocParagraph>
				登録用のコードを設定アプリに貼り付けると、デバイスに
				<DocCode>XXXX-XXXX</DocCode>
				の形の確認コードが表示されます。確認コードは10分間有効です。確認コードを入力する場所は、Izanagiの「デバイスを追加」の手順4だけです。ほかの場所で確認コードを求められても、入力しないでください。
			</DocParagraph>

			<DocSubheading>登録の確認</DocSubheading>
			<DocParagraph>
				手順4で確認コードを入力して「確認」を押すと、確認コード・デバイス名・アカウント名・登録先の組織が表示されます。心当たりのあるデバイスなら「登録」を、違うときは「別のコードを入力」を押します。「「spark-01」を登録しました。」のように表示されたら完了です。
			</DocParagraph>

			<DocHeading id="install">インストールして登録する</DocHeading>
			<DocAudience>組織の管理者向け</DocAudience>
			<DocParagraph>
				デバイスに.debをインストールして、設定アプリで登録します。画面で操作するときは「画面」、コマンドで操作するときは「ターミナル」のタブを見てください。
			</DocParagraph>
			<DocTabs tabs={INSTALL_TABS} framed />
			<DocParagraph>
				登録したデバイスが「オンライン」になったら、エージェントを作成するときに「実行場所」でこのデバイスを選びます。エージェントの作成のしかたは「概要」の「
				<DocSectionLink page="overview" id="getting-started">
					まずはここから
				</DocSectionLink>
				」、起動のしかたは「エージェント」の「
				<DocSectionLink page="agents" id="agent-actions">
					操作ボタン
				</DocSectionLink>
				」を見てください。
			</DocParagraph>

			<DocHeading id="uninstall">デバイスからアンインストールする</DocHeading>
			<DocAudience>組織の管理者向け</DocAudience>
			<DocParagraph>
				アンインストールすると、デバイス上のエージェントのデータはすべて消えます。共有ボリュームを使っているときは、まだ反映していない変更を先に共有ボリュームへ反映しておいてください。
			</DocParagraph>
			<DocList ordered>
				<li>
					エージェントを動かしているアカウントで「Izanagi
					デバイスを登録」を開き、「アンインストール」を押します（ターミナルでは
					<DocCode>u</DocCode>
					キー）。
				</li>
				<li>
					確認すると、設定アプリがエージェントを止め、Izanagiでのこのデバイスの登録を解除し、デバイス上のデータを消します。
				</li>
				<li>最後に管理者のパスワードを入力すると、パッケージも削除されます。</li>
			</DocList>
			<DocParagraph>
				<DocCode>sudo apt remove</DocCode>
				で先にパッケージを消すと、エージェントを止めるときの処理が動きません。必ず設定アプリの「アンインストール」から行ってください。
			</DocParagraph>
			<DocParagraph>
				アンインストールしたあとも、Izanagiのエージェントの一覧にはそのデバイスのエージェントが残ります。設定アプリがIzanagiにつながらず登録を解除できなかったときは、デバイス画面の「解除」で解除してください。
			</DocParagraph>

			<DocSubheading>Izanagiの画面で解除だけするとき</DocSubheading>
			<DocParagraph>
				デバイス画面の一覧の「解除」を押して「解除する」を選ぶと、Izanagiでの登録だけが解除されます。そのデバイスのエージェントは使えなくなり、ライセンスが空きます（「ライセンス」列が「支払い待ち」か「更新日に解除」のときは空きません）。デバイスに入れたソフトウェアは残るので、使わなくなったデバイスは上の手順でアンインストールしてください。
			</DocParagraph>

			<DocHeading id="usb">USB機器やロボットとつなぐ</DocHeading>
			<DocParagraph>
				デバイスにUSBでつないだシリアル機器（ttyACM・ttyUSB。ロボットアームやマイコンなど）とカメラを、1つのエージェントに8台まで渡せます。
			</DocParagraph>

			<DocSubheading>USB機器を使う許可</DocSubheading>
			<DocParagraph>
				設定アプリを最初に開いたときに、USBのシリアル機器とカメラを使う許可を、設定アプリを開いたアカウントに与えるかを聞かれます。「許可する」を選ぶと、管理者のパスワードを入力します。「許可しない」を選んでも、登録はそのまま続きます。あとで許可するには、「Izanagi
				デバイスを登録」を開き直してください。
			</DocParagraph>
			<DocParagraph>
				1台のデバイスのUSB機器を使えるのは、最初に許可した1つのアカウントだけです。
			</DocParagraph>

			<DocSubheading>エージェントに渡す機器を選ぶ</DocSubheading>
			<DocParagraph>
				エージェントの設定の「USB
				機器」タブに、デバイスに差してある機器が表示されます。渡したい機器にチェックを入れて「保存」を押すと、次の起動から渡されます。エージェントの中では、機器がデバイスと同じ名前（
				<DocCode>/dev/ttyACM0</DocCode>や<DocCode>/dev/video0</DocCode>
				など）で見えます。
			</DocParagraph>

			<DocSubheading>同じ機器は同時に使えません</DocSubheading>
			<DocParagraph>
				同じ機器を2つのエージェントに同時に渡すことはできません。ほかのエージェントが使っている機器には、「「エージェントA」が使っています」のように、使っているエージェントの名前が表示されます。
			</DocParagraph>

			<DocSubheading>機器を抜き差ししたとき</DocSubheading>
			<DocParagraph>
				エージェントを起動したあとに機器を抜くと、タブに「抜かれています」と表示されます。差し直すと「差し直されました。使えないときはエージェントを起動し直してください」と表示されます。差し直した機器を使うには、エージェントを起動し直してください。
			</DocParagraph>
			<DocParagraph>
				起動するときに機器を開けないと、「USB
				機器を開けませんでした」と表示されて起動に失敗します。機器がデバイスに差してあるか確かめて、エージェントを起動し直してください。
			</DocParagraph>

			<DocSubheading>ネットワークが切れたとき</DocSubheading>
			<DocParagraph>
				ロボットの推論を続けられるよう、デバイスのネットワークが切れても、デバイスで動いているエージェントは止まりません。オフラインの間はIzanagiからエージェントを止められないので、ロボットを止めたいときは手元の非常停止を使ってください。
			</DocParagraph>
			<DocParagraph>
				ただし、ライセンスの解約を予約したデバイスは、予約の後に一度でもIzanagiにつながっていれば、オフラインのままでも更新日になるとすべてのエージェントを止めます。「支払い待ち」になったデバイスは、Izanagiにつながったときにエージェントが止まります。
			</DocParagraph>
		</DocPage>
	);
}
